/**
 * 行情看板
 * 统计卡 + 价格走势（7/30/90 天）+ 品种均价对比 + 地区均价（热力图降级为表格）+ 免责声明
 */
import { useCallback, useEffect, useState } from 'react';
import { getMarketPrice, getPriceTrend, getQuoteStats, getRegionPrices } from '@/api/market';
import type { MarketPriceItem, PriceTrend, QuoteStats, RegionPriceItem } from '@/types';
import { BREEDS } from '@/utils/constants';
import { formatMoney } from '@/utils/format';
import PriceChart from '@/components/common/PriceChart';
import Loading from '@/components/common/Loading';
import ErrorBox from '@/components/common/ErrorBox';
import PageHero from '@/components/common/PageHero';
import '@/styles/pages/quote.css';

const PERIODS = [
  { key: 7, label: '近 7 天' },
  { key: 30, label: '近 30 天' },
  { key: 90, label: '近 90 天' },
];

export default function Quote() {
  const [breed, setBreed] = useState('全部');
  const [period, setPeriod] = useState(7);
  const [trend, setTrend] = useState<PriceTrend | null>(null);
  const [prices, setPrices] = useState<MarketPriceItem[]>([]);
  const [regionPrices, setRegionPricesData] = useState<RegionPriceItem[]>([]);
  const [stats, setStats] = useState<QuoteStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, r, s] = await Promise.all([
        getMarketPrice(),
        getRegionPrices(),
        getQuoteStats(),
      ]);
      setPrices(p);
      setRegionPricesData(r);
      setStats(s);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (breed === '全部') return;
    getPriceTrend(breed, period)
      .then(setTrend)
      .catch(() => setTrend(null));
  }, [breed, period]);

  const trendData = trend?.points.map((p) => ({ label: p.date.slice(5), value: p.price })) ?? [];

  // 品种均价对比（柱状图）
  const barData = prices.map((p) => ({ label: p.breed, value: p.price }));

  // 地区均价表格（热力图降级）
  const maxRegionPrice = Math.max(...regionPrices.map((r) => r.price), 1);

  return (
    <div className="quote-page">
      <PageHero eyebrow="MARKET QUOTE" title="行情看板" desc="牛只价格参考 · 每日 9:00 更新 · 实际成交以双方协商为准" />

      <div className="container">
        {loading ? (
          <Loading rows={5} />
        ) : error ? (
          <ErrorBox message={error} onRetry={load} />
        ) : (
          <>
            {/* 统计卡 */}
            <div className="quote-stats">
              <div className="quote-stat quote-stat--primary">
                <span>今日均价</span>
                <b>{stats?.today_avg ?? '-'} <em>元/kg</em></b>
              </div>
              <div className="quote-stat">
                <span>较昨日涨跌</span>
                <b className={(stats?.change_pct ?? 0) >= 0 ? 'up' : 'down'}>
                  {(stats?.change_pct ?? 0) >= 0 ? '↑' : '↓'}{Math.abs(stats?.change_pct ?? 0)}%
                </b>
              </div>
              <div className="quote-stat">
                <span>近 7 日成交量</span>
                <b>{stats?.week_volume ?? '-'} <em>头</em></b>
              </div>
              <div className="quote-stat">
                <span>最高品种</span>
                <b>{stats?.max_breed ?? '-'} <em>{formatMoney(stats?.max_price)}/kg</em></b>
              </div>
              <div className="quote-stat">
                <span>最低品种</span>
                <b>{stats?.min_breed ?? '-'} <em>{formatMoney(stats?.min_price)}/kg</em></b>
              </div>
            </div>

            {/* 走势 + 对比 */}
            <div className="quote-charts">
              <div className="quote-card quote-card--wide">
                <div className="quote-card__head">
                  <strong>价格走势</strong>
                  <select
                    className="form-control quote-select"
                    value={breed}
                    onChange={(e) => setBreed(e.target.value)}
                  >
                    <option value="全部">全部品种</option>
                    {BREEDS.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                  <div className="quote-periods">
                    {PERIODS.map((p) => (
                      <button
                        key={p.key}
                        className={`quote-period ${period === p.key ? 'active' : ''}`}
                        onClick={() => setPeriod(p.key)}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
                {breed === '全部' ? (
                  <div className="quote-placeholder">请选择品种查看走势</div>
                ) : trendData.length > 0 ? (
                  <PriceChart type="line" data={trendData} height={280} unit="元/kg" />
                ) : null}
                {trend ? (
                  <div className="quote-trend-info">
                    当前 {formatMoney(trend.current)} 元/kg · 区间涨跌幅 {trend.change_pct >= 0 ? '+' : ''}{trend.change_pct}%
                  </div>
                ) : null}
              </div>

              <div className="quote-card">
                <div className="quote-card__head">
                  <strong>品种均价对比</strong>
                  <span>元/kg</span>
                </div>
                <PriceChart type="bar" data={barData} height={280} color="var(--color-primary)" unit="元/kg" />
              </div>
            </div>

            {/* 地区均价（热力图降级为表格） */}
            <div className="quote-card">
              <div className="quote-card__head">
                <strong>地区均价</strong>
                <span>颜色越深代表价格越高</span>
              </div>
              <div className="quote-region-grid">
                {regionPrices.map((r) => {
                  const intensity = (r.price / maxRegionPrice) * 100;
                  return (
                    <div
                      className="quote-region"
                      key={`${r.city}-${r.breed}`}
                      style={{ background: `rgba(15, 76, 92, ${0.06 + (intensity / 100) * 0.7})` }}
                    >
                      <span className="quote-region__city">{r.city}</span>
                      <span className="quote-region__price">{formatMoney(r.price)}/kg</span>
                      <span className="quote-region__breed">{r.breed}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="quote-disclaimer">
              免责声明：本页价格为平台采集的参考均价，仅作交易决策参考，实际成交以买卖双方协商为准。
            </div>
          </>
        )}
      </div>
    </div>
  );
}
